import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import {
  PaperPlaneTilt, Microphone, Stop, SignOut, SquaresFour,
  Sparkle, Notebook, Plus, Trash, CaretRight, X,
  BookOpen, Clock, ShieldCheck, Paperclip,
} from "@phosphor-icons/react";

const FONT_SERIF = "'Cormorant Garamond', serif";
const FONT_SANS = "'Manrope', system-ui, sans-serif";

// palette aligned with /app/design_guidelines.json
const C = {
  appBg: "#F9F8F6",
  centerBg: "#FFFFFF",
  sidebarBg: "#F3F1EC",
  panelBg: "#F9F8F6",
  userBubble: "#F3F1EC",
  textPrimary: "#1C241C",
  textSecondary: "#5C665A",
  accent: "#9E8155",
  borderSubtle: "#E5E1D8",
};

export default function Chat() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const [bootstrap, setBootstrap] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);   // turns in the active session
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [attachments, setAttachments] = useState([]);   // pending [{id, name}]
  const [uploadingFile, setUploadingFile] = useState(false);
  const fileInputRef = useRef(null);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [focusedTurnId, setFocusedTurnId] = useState(null);
  const [leftOpen, setLeftOpen] = useState(true);

  const recogRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  // ---- bootstrap on mount
  useEffect(() => {
    api.get("/relationship/bootstrap")
      .then(({ data }) => setBootstrap(data))
      .catch(() => toast.error("Couldn't load your profile"));
  }, []);

  // ---- smooth scroll when new messages land
  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const firstName = bootstrap?.user?.first_name || "";
  const profile = bootstrap?.profile || {};
  const sessions = bootstrap?.sessions || [];
  const greeting = bootstrap?.greeting || "Welcome.";

  const focusedTurn = useMemo(
    () => messages.find((m) => m.id === focusedTurnId),
    [messages, focusedTurnId],
  );

  // ---- open a past session
  const openSession = async (sid) => {
    try {
      const { data } = await api.get(`/relationship/sessions/${sid}`);
      const events = data.events || [];
      const turns = [];
      for (const e of events) {
        turns.push({ role: "user", text: e.query, ts: e.created_at });
        turns.push({
          id: e.id,
          role: "brand",
          answer: e.answer,
          followup: e.followup || "",
          sources: [],
          trust: { total_tokens: e.tokens, cost_inr: e.cost_inr },
          remembered_cue: "",
          created_at: e.created_at,
        });
      }
      setMessages(turns);
      setSessionId(sid);
      setFocusedTurnId(null);
      setEvidenceOpen(false);
    } catch {
      toast.error("Couldn't open that conversation");
    }
  };

  const newSession = () => {
    setMessages([]);
    setSessionId(null);
    setFocusedTurnId(null);
    setEvidenceOpen(false);
    inputRef.current?.focus();
  };

  const deleteSession = async (sid, e) => {
    e?.stopPropagation();
    if (!window.confirm("End this conversation? It will be removed from your history.")) return;
    try {
      await api.delete(`/relationship/sessions/${sid}`);
      if (sessionId === sid) newSession();
      const { data } = await api.get("/relationship/bootstrap");
      setBootstrap(data);
      toast.success("Conversation removed");
    } catch {
      toast.error("Couldn't remove");
    }
  };

  // ---- attach files (pack photos / PDFs for complaints)
  const pickFiles = () => fileInputRef.current?.click();

  const onFilesChosen = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 3 - attachments.length);
    e.target.value = "";
    if (!files.length) return;
    setUploadingFile(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        if (sessionId) fd.append("session_id", sessionId);
        const { data } = await api.post("/chat/attachments", fd);
        setAttachments((prev) => [...prev, { id: data.id, name: data.name }]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not upload the file");
    } finally {
      setUploadingFile(false);
    }
  };

  const removeAttachment = (id) => setAttachments((prev) => prev.filter((a) => a.id !== id));

  // ---- send a turn
  const send = async (q) => {
    const query = (q ?? input).trim();
    if ((!query && attachments.length === 0) || busy) return;
    const sentAttachments = attachments;
    setInput("");
    setAttachments([]);
    const displayText = query + (sentAttachments.length
      ? `${query ? "\n" : ""}📎 ${sentAttachments.map((a) => a.name).join(", ")}`
      : "");
    setMessages((m) => [...m, { role: "user", text: displayText, ts: Date.now() }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chat/relationship", {
        query,
        session_id: sessionId || undefined,
        attachment_ids: sentAttachments.length ? sentAttachments.map((a) => a.id) : undefined,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, {
        id: data.id,
        role: "brand",
        answer: data.answer,
        followup: data.followup || "",
        sources: data.sources || [],
        trust: data.trust || {},
        remembered_cue: data.remembered_cue || "",
        created_at: data.created_at,
      }]);
      // Refresh session list so the sidebar shows this conversation
      api.get("/relationship/bootstrap").then(({ data }) => setBootstrap(data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something's off — please try again.");
    } finally {
      setBusy(false);
    }
  };

  // ---- voice
  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Voice isn't supported in this browser"); return; }
    const r = new SR();
    r.continuous = false; r.interimResults = false; r.lang = "en-IN";
    r.onresult = (e) => { const t = e.results[0][0].transcript; setInput(t); setRecording(false); send(t); };
    r.onerror = () => setRecording(false);
    r.onend = () => setRecording(false);
    r.start();
    recogRef.current = r;
    setRecording(true);
  };
  const stopVoice = () => { recogRef.current?.stop(); setRecording(false); };

  // ---- derived: preference chips
  const chips = useMemo(() => {
    const out = [];
    (profile.preferred_brands || []).slice(0, 2).forEach((b) => out.push(b));
    (profile.preferred_topics || []).slice(0, 1).forEach((t) => out.push(t));
    return out;
  }, [profile]);

  // ---- group sessions by day for sidebar
  const sessionGroups = useMemo(() => {
    const groups = {};
    for (const s of sessions) {
      const d = new Date(s.updated_at || s.created_at);
      const key = isToday(d) ? "Today" : isYesterday(d) ? "Yesterday" : d.toLocaleDateString([], { weekday: "long", month: "short", day: "numeric" });
      (groups[key] ||= []).push(s);
    }
    return Object.entries(groups);
  }, [sessions]);

  // conversation-starters (shown only on empty state for first-time users)
  const starters = [
    "Tell me about Aashirvaad Select",
    "Which Sunfeast biscuit is best with tea?",
    "How is Aashirvaad atta sourced?",
    "What should I try if I've never bought ITC before?",
  ];

  const onLogout = () => { logout(); nav("/"); };

  if (!bootstrap) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: C.appBg, fontFamily: FONT_SANS, color: C.textSecondary }}
      >
        <span style={{ fontFamily: FONT_SERIF, fontSize: 22 }} className="italic">
          A moment, please…
        </span>
      </div>
    );
  }

  const conversationCount = profile.conversation_count || 0;

  return (
    <div
      className="h-screen w-screen grid overflow-hidden"
      style={{
        gridTemplateColumns: `${leftOpen ? "290px" : "0px"} 1fr ${evidenceOpen ? "340px" : "0px"}`,
        transition: "grid-template-columns .35s cubic-bezier(.16,1,.3,1)",
        background: C.appBg,
        fontFamily: FONT_SANS,
        color: C.textPrimary,
      }}
      data-testid="chat-layout-wrapper"
    >
      {/* ================= LEFT: sessions ================= */}
      <aside
        className="h-full flex flex-col overflow-hidden"
        style={{ background: C.sidebarBg, borderRight: `1px solid ${C.borderSubtle}` }}
        data-testid="chat-sidebar-left"
      >
        <div className="px-6 pt-7 pb-4">
          <div className="flex items-center gap-2 mb-1" style={{ color: C.accent }}>
            <Sparkle size={14} weight="fill" />
            <span className="text-[11px] tracking-[0.18em] uppercase font-medium">ITC Concierge</span>
          </div>
          <div style={{ fontFamily: FONT_SERIF, fontSize: 26, lineHeight: 1.1, color: C.textPrimary }} className="tracking-tight">
            Your conversations
          </div>
        </div>
        <div className="px-4 pb-3">
          <button
            onClick={newSession}
            data-testid="chat-new-session-btn"
            className="w-full flex items-center justify-between px-4 py-3 transition-all"
            style={{
              background: "#FFFFFF",
              border: `1px solid ${C.borderSubtle}`,
              borderRadius: 10,
              color: C.textPrimary,
            }}
          >
            <span className="flex items-center gap-2 text-[14px]">
              <Plus size={14} /> New conversation
            </span>
            <CaretRight size={12} style={{ color: C.accent }} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-4" data-testid="chat-session-list">
          {sessionGroups.length === 0 ? (
            <div className="px-4 py-8 text-sm italic" style={{ color: C.textSecondary }}>
              Your conversations will live here.
            </div>
          ) : (
            sessionGroups.map(([label, items]) => (
              <div key={label} className="mb-4">
                <div className="px-4 py-2 text-[10px] tracking-[0.18em] uppercase font-medium" style={{ color: C.textSecondary }}>
                  {label}
                </div>
                {items.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => openSession(s.id)}
                    className={`group px-4 py-3 mb-0.5 cursor-pointer transition-all flex items-start gap-2 ${sessionId === s.id ? "" : "hover:bg-white"}`}
                    style={{
                      borderRadius: 8,
                      background: sessionId === s.id ? "#FFFFFF" : "transparent",
                      border: `1px solid ${sessionId === s.id ? C.borderSubtle : "transparent"}`,
                    }}
                    data-testid={`session-item-${s.id}`}
                  >
                    <Notebook size={14} style={{ color: C.accent, marginTop: 3 }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[14px] truncate" style={{ color: C.textPrimary }}>
                        {s.title || "Untitled conversation"}
                      </div>
                      <div className="text-[11px] mt-0.5" style={{ color: C.textSecondary }}>
                        {s.turn_count || 0} {s.turn_count === 1 ? "exchange" : "exchanges"}
                        {s.topic_tags?.[0] ? ` · ${s.topic_tags[0]}` : ""}
                      </div>
                    </div>
                    <button
                      onClick={(e) => deleteSession(s.id, e)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`session-delete-${s.id}`}
                      title="Remove"
                    >
                      <Trash size={12} style={{ color: C.textSecondary }} />
                    </button>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
        <div className="px-4 py-4" style={{ borderTop: `1px solid ${C.borderSubtle}` }}>
          <div className="text-[11px] tracking-[0.12em] uppercase mb-1" style={{ color: C.textSecondary }}>
            Signed in as
          </div>
          <div className="text-[14px] truncate" style={{ color: C.textPrimary }}>
            {user?.email}
          </div>
          <div className="flex items-center gap-2 mt-3">
            {user?.role === "admin" && (
              <Link
                to="/admin"
                data-testid="back-admin-btn"
                className="text-[12px] flex items-center gap-1 px-2 py-1"
                style={{ border: `1px solid ${C.borderSubtle}`, borderRadius: 6, color: C.textPrimary }}
              >
                <SquaresFour size={12} /> Admin
              </Link>
            )}
            <button
              onClick={onLogout}
              data-testid="chat-logout-btn"
              className="text-[12px] flex items-center gap-1 px-2 py-1"
              style={{ border: `1px solid ${C.borderSubtle}`, borderRadius: 6, color: C.textPrimary }}
            >
              <SignOut size={12} /> Sign out
            </button>
          </div>
        </div>
      </aside>

      {/* ================= CENTER: conversation ================= */}
      <section
        className="h-full flex flex-col overflow-hidden"
        style={{ background: C.centerBg }}
        data-testid="chat-center-area"
      >
        {/* header */}
        <header
          className="px-10 py-6 flex items-start justify-between gap-6"
          style={{ borderBottom: `1px solid ${C.borderSubtle}` }}
          data-testid="chat-header"
        >
          <div>
            <div className="flex items-center gap-2 mb-1" style={{ color: C.accent }}>
              <ShieldCheck size={12} weight="fill" />
              <span className="text-[11px] tracking-[0.18em] uppercase font-medium">Relationship Desk</span>
            </div>
            <div
              style={{ fontFamily: FONT_SERIF, fontSize: 28, lineHeight: 1.1, color: C.textPrimary }}
              className="tracking-tight"
              data-testid="chat-header-name"
            >
              Good to see you{firstName ? `, ${firstName}` : ""}.
            </div>
            <div className="text-[13px] mt-1.5 flex items-center gap-3 flex-wrap" style={{ color: C.textSecondary }}>
              <span className="inline-flex items-center gap-1">
                <Clock size={12} />
                Member since {formatMemberSince(profile.first_seen_iso)} · {conversationCount} {conversationCount === 1 ? "conversation" : "conversations"}
              </span>
              {chips.length > 0 && (
                <span className="inline-flex items-center gap-1.5" data-testid="chat-preference-chips">
                  {chips.map((c) => (
                    <span
                      key={c}
                      className="px-2 py-0.5 text-[11px]"
                      style={{ background: C.userBubble, color: C.accent, border: `1px solid ${C.borderSubtle}`, borderRadius: 999 }}
                    >
                      {c}
                    </span>
                  ))}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setLeftOpen((o) => !o)}
              data-testid="chat-toggle-left"
              title={leftOpen ? "Hide conversations" : "Show conversations"}
              className="px-2.5 py-2"
              style={{ border: `1px solid ${C.borderSubtle}`, borderRadius: 8, color: C.textSecondary }}
            >
              <BookOpen size={14} />
            </button>
            <button
              onClick={() => setEvidenceOpen((o) => !o)}
              data-testid="chat-toggle-evidence"
              title="Trust & sources"
              className="px-3 py-2 text-[12px] flex items-center gap-1.5"
              style={{
                border: `1px solid ${evidenceOpen ? C.accent : C.borderSubtle}`,
                borderRadius: 8,
                color: evidenceOpen ? C.accent : C.textSecondary,
                background: evidenceOpen ? "#FFF" : "transparent",
              }}
            >
              <ShieldCheck size={14} />
              Trust
            </button>
          </div>
        </header>

        {/* messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto" data-testid="chat-message-list">
          <div className="max-w-[720px] mx-auto px-10 py-12">
            {messages.length === 0 && (
              <div data-testid="chat-empty">
                <div
                  style={{ fontFamily: FONT_SERIF, fontSize: 40, lineHeight: 1.15, color: C.textPrimary }}
                  className="tracking-tight"
                >
                  {greeting}
                </div>
                <div className="mt-3 text-[15px]" style={{ color: C.textSecondary }}>
                  {conversationCount === 0
                    ? "How may I assist you with your ITC selections today?"
                    : "Tell me what's on your mind — I'll take care of the rest."}
                </div>
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {starters.map((s, i) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`starter-${i}`}
                      className="text-left px-4 py-3.5 text-[14px] transition-all hover:border-[color:var(--accent)]"
                      style={{
                        background: "#FFFFFF",
                        border: `1px solid ${C.borderSubtle}`,
                        borderRadius: 12,
                        color: C.textPrimary,
                        "--accent": C.accent,
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-10">
              {messages.map((m, i) => (
                <div key={m.id || `${m.role}-${i}`}>
                  {m.role === "user" ? (
                    <div className="flex justify-end" data-testid={`user-msg-${i}`}>
                      <div
                        className="px-5 py-3 max-w-[85%] text-[15px] leading-relaxed"
                        style={{ background: C.userBubble, color: C.textPrimary, borderRadius: 16 }}
                      >
                        {m.text}
                      </div>
                    </div>
                  ) : (
                    <div className="group" data-testid={`brand-msg-${i}`}>
                      {m.remembered_cue && (
                        <div
                          className="flex items-center gap-1.5 mb-3 text-[11px]"
                          style={{ color: C.accent }}
                          data-testid={`remembered-cue-${i}`}
                        >
                          <Sparkle size={11} weight="fill" />
                          <span className="italic" style={{ fontFamily: FONT_SERIF, fontSize: 13 }}>
                            {m.remembered_cue}
                          </span>
                        </div>
                      )}
                      <div
                        style={{
                          fontFamily: FONT_SERIF,
                          fontSize: 20,
                          lineHeight: 1.55,
                          color: C.textPrimary,
                        }}
                        className="whitespace-pre-wrap"
                      >
                        {m.answer}
                      </div>
                      {m.followup && (
                        <div
                          className="mt-5 pl-4 flex items-start gap-2"
                          style={{
                            borderLeft: `2px solid ${C.accent}`,
                            color: C.textSecondary,
                            opacity: 0.95,
                            animation: "amex-fade-up .5s cubic-bezier(.16,1,.3,1)",
                          }}
                          data-testid={`followup-${i}`}
                        >
                          <Sparkle size={12} weight="fill" style={{ color: C.accent, marginTop: 5, flexShrink: 0 }} />
                          <span
                            style={{
                              fontFamily: FONT_SANS,
                              fontSize: 13,
                              fontStyle: "italic",
                              lineHeight: 1.5,
                              letterSpacing: "0.01em",
                            }}
                          >
                            {m.followup}
                          </span>
                        </div>
                      )}
                      <div className="mt-4 flex items-center gap-4 flex-wrap">
                        {m.sources && m.sources.length > 0 && (
                          <button
                            onClick={() => { setFocusedTurnId(m.id); setEvidenceOpen(true); }}
                            data-testid={`open-evidence-${i}`}
                            className="text-[12px] flex items-center gap-1.5"
                            style={{ color: C.accent }}
                          >
                            <ShieldCheck size={12} />
                            {m.sources.length} {m.sources.length === 1 ? "reference" : "references"} · view trust
                          </button>
                        )}
                        <span
                          className="text-[11px] font-mono px-2 py-0.5"
                          style={{
                            color: C.accent,
                            background: C.userBubble,
                            border: `1px solid ${C.borderSubtle}`,
                            borderRadius: 999,
                          }}
                          data-testid={`turn-cost-${i}`}
                          title={`${m.trust?.total_tokens || 0} tokens · includes gatekeeper/SOP checks · may still rise slightly once background memory processing finishes (see query log for the final figure)`}
                        >
                          ₹{Number(m.trust?.cost_inr || 0).toFixed(4)}
                        </span>
                        <span className="text-[11px]" style={{ color: C.textSecondary }}>
                          {formatTime(m.created_at)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {busy && (
                <div className="flex items-center gap-2" data-testid="chat-thinking">
                  <span
                    className="italic"
                    style={{
                      fontFamily: FONT_SERIF,
                      fontSize: 18,
                      color: C.textSecondary,
                      animation: "amex-pulse 1.6s ease-in-out infinite",
                    }}
                  >
                    Gathering thoughts…
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* input */}
        <div className="px-10 pb-8 pt-4" style={{ background: C.centerBg }}>
          <div className="max-w-[720px] mx-auto">
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2" data-testid="chat-attachment-chips">
                {attachments.map((a) => (
                  <span
                    key={a.id}
                    className="flex items-center gap-1.5 text-[12px] px-2.5 py-1"
                    style={{ background: C.userBubble, border: `1px solid ${C.borderSubtle}`, borderRadius: 999, color: C.textPrimary }}
                  >
                    <Paperclip size={12} />
                    {a.name}
                    <button onClick={() => removeAttachment(a.id)} title="Remove" style={{ color: C.textSecondary }}>
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div
              className="flex items-end gap-2 px-4 py-3"
              style={{ background: "#FFFFFF", border: `1px solid ${C.borderSubtle}`, borderRadius: 16 }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                multiple
                onChange={onFilesChosen}
                style={{ display: "none" }}
                data-testid="chat-file-input"
              />
              <button
                onClick={pickFiles}
                disabled={busy || uploadingFile || attachments.length >= 3}
                data-testid="chat-attach-btn"
                className="p-1.5 disabled:opacity-40"
                style={{ color: uploadingFile ? C.accent : C.textSecondary }}
                title={attachments.length >= 3 ? "Up to 3 files per message" : "Attach a photo or PDF (max 5 MB)"}
              >
                <Paperclip size={18} />
              </button>
              {!recording ? (
                <button
                  onClick={startVoice}
                  data-testid="chat-voice-start"
                  className="p-1.5"
                  style={{ color: C.textSecondary }}
                  title="Speak your question"
                >
                  <Microphone size={18} />
                </button>
              ) : (
                <button onClick={stopVoice} data-testid="chat-voice-stop" className="p-1.5" style={{ color: C.accent }}>
                  <Stop size={18} weight="fill" />
                </button>
              )}
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Ask me anything — in your own words."
                rows={1}
                data-testid="chat-input"
                className="flex-1 resize-none outline-none bg-transparent text-[15px] py-1.5"
                style={{ color: C.textPrimary, fontFamily: FONT_SANS, maxHeight: 160 }}
                disabled={busy}
              />
              <button
                onClick={() => send()}
                disabled={busy || (!input.trim() && attachments.length === 0)}
                data-testid="chat-send-btn"
                className="flex items-center gap-1.5 px-4 py-2 text-[13px] disabled:opacity-40 transition-all"
                style={{ background: C.textPrimary, color: "#FFFFFF", borderRadius: 10 }}
              >
                <PaperPlaneTilt size={14} weight="fill" />
                Send
              </button>
            </div>
            <div className="text-[11px] mt-2.5 text-center italic" style={{ color: C.textSecondary, fontFamily: FONT_SERIF, fontSize: 13 }}>
              Every exchange is kept in confidence — you may clear it at any moment.
            </div>
          </div>
        </div>
      </section>

      {/* ================= RIGHT: evidence panel ================= */}
      <aside
        className="h-full overflow-hidden"
        style={{ background: C.panelBg, borderLeft: `1px solid ${C.borderSubtle}` }}
        data-testid="chat-evidence-panel"
      >
        {evidenceOpen && (
          <div className="h-full flex flex-col">
            <div className="px-6 pt-7 pb-4 flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1" style={{ color: C.accent }}>
                  <ShieldCheck size={12} weight="fill" />
                  <span className="text-[11px] tracking-[0.18em] uppercase font-medium">Trust & Sources</span>
                </div>
                <div style={{ fontFamily: FONT_SERIF, fontSize: 22, color: C.textPrimary }} className="tracking-tight">
                  Where this came from
                </div>
              </div>
              <button
                onClick={() => setEvidenceOpen(false)}
                data-testid="evidence-close"
                className="p-1.5"
                style={{ color: C.textSecondary }}
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-6 pb-6">
              {!focusedTurn ? (
                <div className="text-[14px] italic" style={{ fontFamily: FONT_SERIF, fontSize: 16, color: C.textSecondary }}>
                  Select any of my replies to see the references I drew on, along with a brief audit trail.
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <div className="text-[11px] tracking-[0.12em] uppercase mb-2" style={{ color: C.textSecondary }}>
                      You asked
                    </div>
                    <div className="text-[13px] p-3" style={{ background: "#FFFFFF", border: `1px solid ${C.borderSubtle}`, borderRadius: 8, color: C.textPrimary }}>
                      {messages[messages.findIndex((mm) => mm.id === focusedTurn.id) - 1]?.text || "—"}
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="text-[11px] tracking-[0.12em] uppercase mb-2" style={{ color: C.textSecondary }}>
                      References consulted ({focusedTurn.sources?.length || 0})
                    </div>
                    <div className="space-y-2" data-testid="evidence-sources">
                      {(focusedTurn.sources || []).map((s, si) => (
                        <div
                          key={s.knowledge_id || si}
                          className="p-3"
                          style={{ background: "#FFFFFF", border: `1px solid ${C.borderSubtle}`, borderRadius: 8 }}
                        >
                          <div className="flex items-start gap-2 mb-1">
                            <span
                              className="text-[10px] px-1.5 py-0.5 font-medium"
                              style={{ background: C.textPrimary, color: "#FFF", borderRadius: 4 }}
                            >
                              {si + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-[13px] font-medium" style={{ color: C.textPrimary }}>
                                {s.title}
                              </div>
                              <div className="text-[11px] mt-0.5" style={{ color: C.textSecondary }}>
                                {s.location_path}
                              </div>
                            </div>
                            <span className="text-[10px]" style={{ color: C.accent }}>
                              relevance {Number(s.score || 0).toFixed(2)}
                            </span>
                          </div>
                          {s.snippet && (
                            <div className="text-[12px] mt-1.5" style={{ color: C.textSecondary }}>
                              {s.snippet}
                            </div>
                          )}
                        </div>
                      ))}
                      {(focusedTurn.sources || []).length === 0 && (
                        <div className="text-[12px] italic" style={{ color: C.textSecondary }}>
                          No specific references were used for this reply.
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <div className="text-[11px] tracking-[0.12em] uppercase mb-2" style={{ color: C.textSecondary }}>
                      Audit trail
                    </div>
                    <div className="p-3 text-[11px] font-mono" style={{ background: "#FFFFFF", border: `1px solid ${C.borderSubtle}`, borderRadius: 8, color: C.textSecondary }}>
                      <div>tokens · {focusedTurn.trust?.total_tokens || 0}</div>
                      <div>cost (at reply time) · ₹{Number(focusedTurn.trust?.cost_inr || 0).toFixed(4)}</div>
                      <div>turn · {focusedTurn.id?.slice(0, 8) || "—"}</div>
                      <div className="mt-1.5 text-[10px] opacity-70">
                        Includes gatekeeper + SOP checks for this turn. Background memory
                        processing may add a small amount after — see Admin → Query Logs for the final total.
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </aside>

      {/* inline CSS for the calm pulse */}
      <style>{`
        @keyframes amex-pulse {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }
        @keyframes amex-fade-up {
          0% { opacity: 0; transform: translateY(6px); }
          100% { opacity: 0.95; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// helpers
function isToday(d) {
  const n = new Date();
  return d.toDateString() === n.toDateString();
}
function isYesterday(d) {
  const n = new Date();
  n.setDate(n.getDate() - 1);
  return d.toDateString() === n.toDateString();
}
function formatTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); } catch { return ""; }
}
function formatMemberSince(iso) {
  if (!iso) return "today";
  try { return new Date(iso).toLocaleDateString([], { month: "short", year: "numeric" }); } catch { return "today"; }
}
